import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, Link, useSearchParams } from 'react-router-dom';
import SEO from './components/SEO';
import './Styles/Blog.css';

function Blog() {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const category = searchParams.get('category');
    const shouldDisplay = location.pathname === '/blog/' || location.pathname === '/blog';
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        setIsLoading(true);
        setError(null);

        fetch('/articles/index.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch articles');
                }
                return response.json();
            })
            .then(data => {
                const sortedArticles = data.articles.sort((a, b) => new Date(b.date) - new Date(a.date));
                setArticles(sortedArticles);
                // Extract unique categories
                const uniqueCategories = [...new Set(data.articles.map(article => article.category))];
                setCategories(uniqueCategories);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error:', error);
                setError(error.message);
                setIsLoading(false);
            });
    }, []);

    const filteredArticles = articles.filter(article => {
        const matchesCategory = !category || article.category === category;
        const matchesSearch = !searchQuery || 
            article.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.category.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const metaTitle = category ? `${category} - Blog - Oleg Orlenko` : "Blog - Oleg Orlenko";
    const metaDescription = category 
        ? `Explore ${category} articles and insights from Oleg Orlenko.`
        : "Explore insights on technology, evolution, and intelligence through thought-provoking articles.";
    const metaKeywords = category 
        ? `${category.toLowerCase()}, blog, articles, insights`
        : "blog, technology, evolution, intelligence, articles";
    const url = category ? `/blog?category=${category}` : "/blog";
        
    return (
        <div>
            <Outlet />
            {shouldDisplay && (
                <>
                    <SEO
                        title={metaTitle}
                        description={metaDescription}
                        keywords={metaKeywords}
                        url={url}
                        type="blog"
                    />
                    <div className="blogs-container">
                        {/* Search and Category Filter */}
                        <div className="blog-controls">
                            <div className="search-box">
                                <input
                                    type="text"
                                    placeholder="Search articles..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                            <div className="category-filter">
                                <Link 
                                    to="/blog" 
                                    className={`category-link ${!category ? 'active' : ''}`}
                                >
                                    All
                                </Link>
                                {categories.map(cat => (
                                    <Link
                                        key={cat}
                                        to={`/blog?category=${cat}`}
                                        className={`category-link ${category === cat ? 'active' : ''}`}
                                    >
                                        {cat}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="loading">Loading articles...</div>
                        ) : error ? (
                            <div className="error">Error loading articles: {error}</div>
                        ) : (
                            <div className="articles-grid">
                                {filteredArticles.map((post) => (
                                    <div className="post" key={post.slug}>
                                        {post.image && (
                                            <div className="post-image">
                                                <img 
                                                    src={post.image} 
                                                    alt={post.name}
                                                    loading="lazy"
                                                    width="300"
                                                    height="200"
                                                />
                                            </div>
                                        )}
                                        <div className="post-category">{post.category}</div>
                                        <div className="post-content">
                                            <h2><Link to={post.slug}>{post.name}</Link></h2>
                                            <p>{post.description}</p>
                                            <div className="post-meta">
                                                <span className="date">Published on {post.date}</span>
                                                <span className="reading-time">{post.readingTime}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default Blog;